import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Module } from "@/constants";
import { useToast } from "@/components/ui/use-toast";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { getApiErrorDescription } from "@/lib/utils/getApiErrorDescription";
// `import type`: services/api ciągnie AppCfg (Cognito); jako typ nie trafia do bundla testów.
import type { ApiError } from "@/services/api";
import {
  requestAgreementFileUrl,
  type AgreementFileUrlRequest,
  type AgreementFileUrlResponse,
} from "@/services/agreements";
import type { ReadAgreementDetail } from "@/schemas/agreement";
import { bumpFileDownloadTotal } from "./useAgreementFileDownloads";
import { silentlyRefreshAgreement } from "./useSilentAgreementRefresh";
import type {
  AttachmentFileAccessMode,
  AttachmentFileItem,
  AttachmentFileUrlResolver,
} from "@/components/AttachmentsDisplaySection";

/** `TError` mutacji to ApiError, ale w praktyce trafia tu też TypeError (sieć) albo ZodError (kształt). */
const isApiError = (error: unknown): error is ApiError =>
  typeof error === "object" &&
  error !== null &&
  "errorCode" in error &&
  typeof (error as { errorCode?: unknown }).errorCode === "number";

/**
 * Adresy do plików umowy (PDF-y i skany) na żądanie przez `POST …/files/download-url`
 * (ASMMNT-83, etap 2). Backend zapisuje zdarzenie pobrania; po sukcesie dopisujemy je do
 * `file_downloads` (i agregatu `file_download_totals`) w cache szczegółów przez `setQueryData` —
 * invalidacja szczegółów zamieniłaby całą stronę w loader (`useAgreementLoading`). Wyjątek: 404
 * (plik albo umowa zniknęły spod nieświeżej strony) odświeża szczegóły, też po cichu.
 *
 * @param agreementId ULID umowy z trasy (klucz cache szczegółów)
 * @returns `resolveFileUrl` w kontrakcie `AttachmentsDisplaySection` (`null` = błąd już zakomunikowany)
 */
export const useAgreementFileUrl = (agreementId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  const mutation = useMutationWithErrorHandling<
    AgreementFileUrlResponse,
    ApiError,
    AgreementFileUrlRequest,
    unknown
  >({
    mutationFn: (request) => requestAgreementFileUrl(agreementId, request),
    onSuccess: ({ event }) => {
      queryClient.setQueryData<ReadAgreementDetail>(
        [Module.Agreements, agreementId],
        (previous) =>
          previous
            ? {
                ...previous,
                // Cache sprzed etapu 2 może nie mieć listy — wtedy ją tworzymy.
                file_downloads: [...(previous.file_downloads ?? []), event],
                // Agregat tylko, gdy backend go zna; bez niego front liczy z listy.
                ...(previous.file_download_totals && {
                  file_download_totals: bumpFileDownloadTotal(
                    previous.file_download_totals,
                    event,
                  ),
                }),
              }
            : previous,
      );
    },
    onError: (error) => {
      const apiError = isApiError(error) ? error : null;
      if (apiError?.errorCode === 404) {
        // Plik albo umowa zniknęły spod nieświeżej strony: ciche odświeżenie, bez loadera całej strony.
        void silentlyRefreshAgreement(queryClient, agreementId);
      }
      toast({
        title: t("agreements.fileDownloads.error"),
        // Błąd spoza API (sieć, zły kształt odpowiedzi) dostaje ogólny tekst, nie JSON z wyjątku.
        description: apiError
          ? getApiErrorDescription(apiError, t)
          : t("errors.unknownError"),
        variant: "destructive",
      });
    },
  });

  const { mutateAsync } = mutation;

  const resolveFileUrl = useCallback<AttachmentFileUrlResolver>(
    async (file: AttachmentFileItem, mode: AttachmentFileAccessMode) => {
      if (!file.path) return null;
      try {
        const { url } = await mutateAsync({ path: file.path, mode });
        return url;
      } catch {
        // Błąd został zakomunikowany w `onError` (toast, ewentualna invalidacja);
        // kontrakt resolvera: `null` = nie otwieraj/nie pobieraj, użytkownik już wie.
        return null;
      }
    },
    [mutateAsync],
  );

  return { resolveFileUrl };
};
